/**
 * Unified core types for the DeltaBase event sourcing ecosystem
 *
 * This is the single source of truth for all event sourcing types across
 * the entire DeltaBase platform. All packages import from here.
 */

// ========================================
// Foundation Types
// ========================================

/**
 * Default record type for structured data
 */
export type DefaultRecord = Record<string, unknown>;

/**
 * Stream position within a specific stream (0-based)
 */
export type StreamPosition = number;

/**
 * Global position across all streams in the event store
 */
export type GlobalPosition = number;

/**
 * Stream identifier type for type safety
 */
export type StreamId = string;

// ========================================
// Event System Types
// ========================================

/**
 * Platform-level metadata for infrastructure concerns
 */
export interface PlatformEventMetadata {
  /** Schema version for backward compatibility and evolution */
  schemaVersion?: string;
  /** Transaction ID for tracing and correlation */
  transactionId?: string;
  /** Message ID for deduplication and tracking */
  messageId?: string;
  /** Additional custom metadata */
  [key: string]: unknown;
}

/**
 * Event as sent to the event store (input format)
 *
 * This is the canonical Event type used throughout the DeltaBase ecosystem.
 * Optimized for type safety, developer experience, and performance.
 *
 * Enhanced to support both application-level and platform-level metadata.
 */
export type Event<
  EventType extends string = string,
  EventData extends DefaultRecord = DefaultRecord,
  EventMetadata extends DefaultRecord | undefined = undefined,
> = Readonly<
  EventMetadata extends undefined
    ? {
        type: EventType;
        data: EventData;
        /** Platform-level metadata for infrastructure concerns */
        metadata?: PlatformEventMetadata;
      }
    : {
        type: EventType;
        data: EventData;
        /** Application-level metadata merged with platform metadata */
        metadata: EventMetadata & PlatformEventMetadata;
      }
>;

/**
 * Event as read from the event store (output format)
 *
 * Includes system fields for tracking and tracing.
 * Type is distributive for perfect union type handling.
 */
export type ReadEvent<EventType extends Event = Event> = EventType extends Event
  ? {
      // Core event fields
      type: EventTypeOf<EventType>;
      data: EventDataOf<EventType>;

      // System tracking fields
      streamId: StreamId;
      streamPosition: StreamPosition;
      globalPosition: GlobalPosition;
      eventId: string;
      schemaVersion: string;
      transactionId: string;
      createdAt: string;

      // Optional metadata
      metadata?: EventMetadataOf<EventType>;
    }
  : never;

// Event utility types for type-safe access
export type EventTypeOf<T extends Event> = T['type'];
export type EventDataOf<T extends Event> = T['data'];
export type EventMetadataOf<T extends Event> = T extends { metadata: infer M }
  ? M
  : undefined;

/**
 * Any event type for generic contexts
 */
export type AnyEvent = Event<string, DefaultRecord, DefaultRecord | undefined>;

/**
 * Type-safe event factory function
 */
export const event = <EventType extends Event>(
  ...args: EventMetadataOf<EventType> extends undefined
    ? [type: EventTypeOf<EventType>, data: EventDataOf<EventType>]
    : [
        type: EventTypeOf<EventType>,
        data: EventDataOf<EventType>,
        metadata: EventMetadataOf<EventType>,
      ]
): EventType => {
  const [type, data, metadata] = args;
  return metadata !== undefined
    ? ({ type, data, metadata } as unknown as EventType)
    : ({ type, data } as unknown as EventType);
};
/**
 * Command type for representing intent to perform actions
 *
 * @template CommandType - The command type identifier
 * @template CommandData - The command payload data type
 * @template CommandMetadata - Optional metadata type
 */
export type Command<
  CommandType extends string = string,
  CommandData extends DefaultRecord = DefaultRecord,
  CommandMetadata extends DefaultRecord | undefined = undefined,
> = Readonly<
  CommandMetadata extends undefined
    ? {
        type: CommandType;
        data: CommandData;
      }
    : {
        type: CommandType;
        data: CommandData;
        metadata: CommandMetadata;
      }
> & { readonly kind?: 'Command' };

// Utility types for accessing Command properties
export type CommandTypeOf<T extends Command> = T['type'];
export type CommandDataOf<T extends Command> = T['data'];
export type CommandMetadataOf<T extends Command> = T extends {
  metadata: infer M;
}
  ? M
  : undefined;

/**
 * Helper for creating command instances
 */

export const command = <CommandType extends Command>(
  ...args: CommandMetadataOf<CommandType> extends undefined
    ? [type: CommandTypeOf<CommandType>, data: CommandDataOf<CommandType>]
    : [
        type: CommandTypeOf<CommandType>,
        data: CommandDataOf<CommandType>,
        metadata: CommandMetadataOf<CommandType>,
      ]
): CommandType => {
  const [type, data, metadata] = args;
  return metadata !== undefined
    ? ({ type, data, metadata } as unknown as CommandType)
    : ({ type, data } as unknown as CommandType);
};

/**
 * Generic command type for use in generic contexts
 */
export type AnyCommand = Command<
  string,
  DefaultRecord,
  DefaultRecord | undefined
>;
/**
 * Result of command processing
 */
export type CommandResult<T = unknown> = {
  /** Whether the command was successful */
  readonly success: boolean;
  /** Result data if successful */
  readonly data?: T;
  /** Error information if unsuccessful */
  readonly error?: {
    readonly message: string;
    readonly code?: string;
    readonly details?: DefaultRecord;
  };
  /** Events that were generated as a result of this command */
  readonly events: ReadEvent[];
};

/**
 * Generic message type for workflow systems
 */
export type Message<
  MessageType extends string = string,
  MessageData extends DefaultRecord = DefaultRecord,
  MessageMetadata extends DefaultRecord | undefined = undefined,
> = Readonly<
  MessageMetadata extends undefined
    ? {
        type: MessageType;
        data: MessageData;
        messageId: string;
        createdAt: string;
        correlationId?: string;
        causationId?: string;
      }
    : {
        type: MessageType;
        data: MessageData;
        metadata: MessageMetadata;
        messageId: string;
        createdAt: string;
        correlationId?: string;
        causationId?: string;
      }
> & { readonly kind?: 'Message' };

// Utility types for accessing Message properties
export type MessageTypeOf<T extends Message> = T['type'];
export type MessageDataOf<T extends Message> = T['data'];
export type MessageMetadataOf<T extends Message> = T extends {
  metadata: infer M;
}
  ? M
  : undefined;

// ========================================
// Stream Version Management
// ========================================

/**
 * Expected stream version for optimistic concurrency control
 *
 * Unified version that combines the best patterns from all packages
 */
export type ExpectedStreamVersion<StreamVersion = StreamPosition> =
  | StreamVersion // Specific version number
  | 'no_stream' // Stream must not exist
  | 'stream_exists' // Stream must exist (any version)
  | 'any'; // No version check

// Constants for version management
export const STREAM_EXISTS = 'stream_exists' as const;
export const STREAM_DOES_NOT_EXIST = 'no_stream' as const;
export const NO_CONCURRENCY_CHECK = 'any' as const;

/**
 * Utility function for version matching logic
 */
export const matchesExpectedVersion = <StreamVersion = StreamPosition>(
  current: StreamVersion | undefined,
  expected: ExpectedStreamVersion<StreamVersion>,
  defaultVersion: StreamVersion
): boolean => {
  if (expected === NO_CONCURRENCY_CHECK) return true;
  if (expected === STREAM_DOES_NOT_EXIST) return current === defaultVersion;
  if (expected === STREAM_EXISTS) return current !== defaultVersion;
  return current === expected;
};

// ========================================
// Event Store Operations
// ========================================

/**
 * Options for reading events from a stream
 */
export type ReadStreamOptions<StreamVersion = StreamPosition> = (
  | { from: StreamVersion }
  | { to: StreamVersion }
  | { from: StreamVersion; maxCount?: number }
  | { expectedStreamVersion: ExpectedStreamVersion<StreamVersion> }
) & {
  expectedStreamVersion?: ExpectedStreamVersion<StreamVersion>;
};

/**
 * Result of reading events from a stream
 */
export type ReadStreamResult<
  EventType extends Event = AnyEvent,
  StreamVersion = StreamPosition,
> = {
  currentStreamVersion: StreamVersion;
  events: ReadEvent<EventType>[];
  streamExists: boolean;
};

/**
 * Options for appending events to a stream
 */
export type AppendToStreamOptions<StreamVersion = StreamPosition> = {
  expectedStreamVersion?: ExpectedStreamVersion<StreamVersion>;
};

/**
 * Result of appending events to a stream
 */
export type AppendToStreamResult<StreamVersion = StreamPosition> = {
  nextExpectedStreamVersion: StreamVersion;
  createdNewStream: boolean;
};

/**
 * Options for aggregating events into state
 */
export type AggregateStreamOptions<
  State,
  EventType extends Event,
  StreamVersion = StreamPosition,
> = {
  evolve: (currentState: State, event: ReadEvent<EventType>) => State;
  initialState: () => State;
  read?: ReadStreamOptions<StreamVersion>;
};

/**
 * Result of aggregating a stream
 */
export type AggregateStreamResult<State, StreamVersion = StreamPosition> = {
  currentStreamVersion: StreamVersion;
  state: State;
  streamExists: boolean;
};

// ========================================
// Query System Types
// ========================================

/**
 * Query parameters for filtering events across streams
 */
export type QueryEventsOptions = {
  /** Filter by specific stream ID */
  streamId?: string;
  /** Filter by event type(s) */
  type?: string | string[];
  /** Filter by specific event ID */
  eventId?: string;
  /** Filter by transaction ID */
  transactionId?: string;
  /** Filter by minimum global position */
  fromPosition?: number;
  /** Filter by maximum global position */
  toPosition?: number;
  /** Filter by minimum creation date (ISO string) */
  fromDate?: string;
  /** Filter by maximum creation date (ISO string) */
  toDate?: string;
  /** Filter by event phase */
  phase?: number;
  /** Whether to include archived events */
  includeArchived?: boolean;
  /** Maximum number of events to return */
  limit?: number;
  /** Number of events to skip */
  offset?: number;
  /** Field to sort results by */
  sortBy?: 'globalPosition' | 'createdAt' | 'streamPosition';
  /** Direction to sort results */
  sortDirection?: 'asc' | 'desc';
  /** Whether to include total count in results */
  includeCount?: boolean;
};

/**
 * Result of a query events operation
 */
export type QueryEventsResult<EventType extends Event = Event> = {
  /** Array of matching events */
  events: ReadEvent<EventType>[];
  /** Pagination information */
  pagination: {
    /** Number of events returned */
    limit: number;
    /** Number of events skipped */
    offset: number;
    /** Total number of matching events (if includeCount was true) */
    total?: number;
    /** Whether there are more results available */
    hasMore: boolean;
  };
};

// ========================================
// EventStore Interface
// ========================================

/**
 * Unified EventStore interface combining the best patterns from all packages
 *
 * This is the canonical interface for all event store implementations
 */
export interface EventStore<StreamVersion = StreamPosition> {
  /**
   * Aggregate events from a stream to rebuild state
   */
  aggregateStream<State, EventType extends Event>(
    streamId: StreamId,
    options: AggregateStreamOptions<State, EventType, StreamVersion>
  ): Promise<AggregateStreamResult<State, StreamVersion>>;

  /**
   * Read events from a stream with filtering and pagination
   */
  readStream<EventType extends Event>(
    streamId: StreamId,
    options?: ReadStreamOptions<StreamVersion>
  ): Promise<ReadStreamResult<EventType, StreamVersion>>;

  /**
   * Append events to a stream with optimistic concurrency control
   */
  appendToStream<EventType extends Event>(
    streamId: StreamId,
    events: EventType[],
    options?: AppendToStreamOptions<StreamVersion>
  ): Promise<AppendToStreamResult<StreamVersion>>;

  /**
   * Query events across streams with flexible filtering options
   */
  queryEvents<EventType extends Event>(
    options?: QueryEventsOptions
  ): Promise<QueryEventsResult<EventType>>;
}

/**
 * Stream metadata information
 */
export type StreamMetadata<StreamVersion = StreamPosition> = {
  /** The stream identifier */
  readonly streamId: StreamId;
  /** Current version of the stream */
  readonly version: StreamVersion;
  /** When the stream was created */
  readonly createdAt: string;
  /** When the stream was last updated */
  readonly updatedAt: string;
  /** Custom metadata for the stream */
  readonly metadata: DefaultRecord;
};
